import type { ListOfTypes } from '@/modules/matrix/interfaces/store.interface'

export type LevelForm = 'three' | 'six' | 'pass' | 'later'

export type MatrixLevelStatus = 'open' | 'pending' | 'activate' | 'locked'

export interface MatrixLevelDescriptor {
  id: string
  type: string
  price: number
  currency: string
  form: LevelForm
}

const CURRENCY = 'GNK'

export const LEVELS: MatrixLevelDescriptor[] = [
  { id: 'F0', type: 'gonka-game_f0', price: 20, currency: CURRENCY, form: 'later' },
  { id: 'F1', type: 'gonka-game_f1', price: 40, currency: CURRENCY, form: 'six' },
  { id: 'F2', type: 'gonka-game_f2', price: 100, currency: CURRENCY, form: 'pass' },
  { id: 'F3', type: 'gonka-game_f3', price: 260, currency: CURRENCY, form: 'pass' },
  { id: 'F4', type: 'gonka-game_f4', price: 660, currency: CURRENCY, form: 'three' },
  { id: 'F5', type: 'gonka-game_f5', price: 1460, currency: CURRENCY, form: 'pass' },
  { id: 'F6', type: 'gonka-game_f6', price: 3000, currency: CURRENCY, form: 'three' },
  { id: 'F7', type: 'gonka-game_f7', price: 6000, currency: CURRENCY, form: 'pass' },
  { id: 'F8', type: 'gonka-game_f8', price: 10600, currency: CURRENCY, form: 'three' },
  { id: 'F9', type: 'gonka-game_f9', price: 18600, currency: CURRENCY, form: 'pass' },
  { id: 'F10', type: 'gonka-game_f10', price: 30000, currency: CURRENCY, form: 'three' },
  { id: 'F11', type: 'gonka-game_f11', price: 40000, currency: CURRENCY, form: 'pass' },
  { id: 'F12', type: 'gonka-game_f12', price: 80000, currency: CURRENCY, form: 'pass' },
]

export function normalizeLevelId(levelId: string): string {
  const up = levelId.trim().toUpperCase()
  // Legacy alias: R-ids lived in old deep links; F is the public numbering (D3).
  return up.startsWith('R') ? `F${up.slice(1)}` : up
}

export function getLevelById(levelId: string): MatrixLevelDescriptor | undefined {
  const normalized = normalizeLevelId(levelId)
  return LEVELS.find(level => level.id === normalized)
}

/** Preview geometry is independent of whether a flow is bought or passed. */
export function getLevelCellCount(level: MatrixLevelDescriptor | null | undefined): number {
  if (!level) return 0
  if (level.id === 'F0') return 2
  return ['F1', 'F2', 'F3'].includes(level.id) ? 6 : 3
}

export function getMatrixTypeByLevelId(levelId: string): string | null {
  return getLevelById(levelId)?.type ?? null
}

export function getPublicLevelIdByMatrixType(matrixType: string): string | null {
  return LEVELS.find(level => level.type === matrixType)?.id ?? null
}

export function getMatrixUrlByLevelId(levelId: string): string {
  return `/chains/${normalizeLevelId(levelId)}`
}

/**
 * Neighbouring level id in feed order (no wrap-around).
 * dir = 1 -> next level, dir = -1 -> previous level. Returns null at the edges.
 */
export function getAdjacentLevelId(levelId: string, dir: 1 | -1): string | null {
  const index = LEVELS.findIndex(level => level.id === normalizeLevelId(levelId))
  if (index === -1) return null
  const target = index + dir
  if (target < 0 || target >= LEVELS.length) return null
  return LEVELS[target].id
}

export function getMatrixLevelStatus(
  level: MatrixLevelDescriptor,
  listOfTypes: Pick<ListOfTypes, 'opened' | 'pending'> | null | undefined,
): MatrixLevelStatus {
  if (listOfTypes?.opened?.includes(level.type)) {
    return 'open'
  }

  if (Object.prototype.hasOwnProperty.call(listOfTypes?.pending ?? {}, level.type)) {
    return 'pending'
  }

  if (level.form === 'pass' || level.form === 'later') {
    return 'locked'
  }

  return 'activate'
}

/** Levels shown in the rail/feed: F0 (form 'later') is permanently hidden. */
export function getVisibleLevels(): MatrixLevelDescriptor[] {
  return LEVELS.filter(level => level.form !== 'later')
}

/** Display-only level count; does not require an authenticated session. */
export function getCurrentLevel(listOfTypes?: { opened?: string[] | null } | null): number {
  return Array.isArray(listOfTypes?.opened) ? listOfTypes.opened.length : 0
}

/**
 * Покупаемость потока по его составу: проходные ('pass') открываются сами по
 * ходу игры, отложенный F0 ('later') не продаётся вовсе.
 *
 * Единственный критерий на весь пакет: и список выбора, и корзина зовут его,
 * иначе они разойдутся в понимании того, что можно купить. Состояние человека
 * (куплен, предоплачен, стоит в очереди) считает useFlowBasket — здесь только
 * состав уровня.
 */
export function isPurchasableLevel(level: MatrixLevelDescriptor): boolean {
  return level.form !== 'pass' && level.form !== 'later'
}

/** Потоки, которые продаются: F1, F4, F6, F8, F10. */
export function getPurchasableLevels(): MatrixLevelDescriptor[] {
  return LEVELS.filter(isPurchasableLevel)
}

/**
 * Suggested dashboard level: the highest opened level, else the first level
 * available for activation, else F1. The canonical /chains entry always uses F1.
 */
export function resolveEntryLevelId(
  listOfTypes: Pick<ListOfTypes, 'opened' | 'pending'> | null | undefined,
): string {
  const visible = getVisibleLevels()
  const opened = visible.filter(level => listOfTypes?.opened?.includes(level.type))
  if (opened.length) return opened[opened.length - 1].id
  const activatable = visible.find(
    level => getMatrixLevelStatus(level, listOfTypes) === 'activate',
  )
  return activatable?.id ?? visible[0].id
}
